import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import AnimatedLine from '../components/AnimatedLine';
import AnimatedVerticalLine from '../components/AnimatedVerticalLine';
import PixelBlast from '../components/PixelBlast';
import TypewriterText from '../components/TypewriterText';
import { FocusCards } from '../components/ui/focus-cards';
import { ChevronDown, Play } from 'lucide-react';

const Alt1Landing = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [showContent, setShowContent] = useState(false);
  const [typingComplete, setTypingComplete] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const portfolioClients = [
    "https://framerusercontent.com/images/Bjz8UgHPdIAda9n6I2exkkpFk.png?width=1200&height=155",
    "https://framerusercontent.com/images/9okGgKRQvft6nApRWLVOqnt9YOk.png?width=2400&height=797",
    "https://framerusercontent.com/images/6WTYWH6wX2rnWpBfvYNpuRsbL2c.png?width=442&height=150",
    "https://framerusercontent.com/images/4BTH4HuoCW1z5rGc7nCORRX2KPU.png?width=881&height=323",
    "https://framerusercontent.com/images/P20Csz2Tx8IOEJY5JaJxyr6J0M.png?width=336&height=104",
    "https://framerusercontent.com/images/VPooO8yNuf4jzcTTtIxO1z15G54.png?width=8158&height=1621"
  ];

  const caseStudies = [
    {
      title: "Tech Founder → 100K Subscribers",
      src: "https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=3270&auto=format&fit=crop",
      slug: "tech-founder-100k-subscribers"
    },
    {
      title: "VC Firm → 50M+ Views",
      src: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=3415&auto=format&fit=crop",
      slug: "vc-firm-50m-views"
    },
    {
      title: "Enterprise → $2M ARR from Content",
      src: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?q=80&w=3270&auto=format&fit=crop",
      slug: "enterprise-2m-arr-content"
    },
  ];

  const videos = [
    { id: "l2lBQ9WzWuY", thumbnail: "https://i.ytimg.com/vi_webp/l2lBQ9WzWuY/sddefault.webp" },
    { id: "MAgkb27U6i0", thumbnail: "https://i.ytimg.com/vi_webp/MAgkb27U6i0/sddefault.webp" },
    { id: "qsgKmij7guQ", thumbnail: "https://i.ytimg.com/vi_webp/qsgKmij7guQ/sddefault.webp" }
  ];

  const services = [
    {
      title: "The Protocol Sprint",
      subtitle: "The Wedge",
      description: "For skeptics and data-seekers. A 30-day rapid deployment to benchmark your \"Attention Index.\"",
      includes: [
        "1 Strategy Workshop (The Audit)",
        "4 Long-form Video Assets",
        "10 Short-form Vertical Assets",
        "The Lab Report: A forensic analysis of your current market position"
      ],
      price: "$3,000",
      priceNote: "One-time",
      cta: "Initialize Sprint"
    },
    {
      title: "The Founder OS",
      subtitle: "The Engine",
      description: "For founders scaling trust. We become your external Chief Media Officer.",
      includes: [
        "Full Channel Management (YouTube, LinkedIn, X)",
        "Monthly \"Extraction\" Interview",
        "30+ Assets per month (Video, Text, Newsletter)",
        "Community Management & Response"
      ],
      price: "$6,000",
      priceNote: "per month",
      cta: "Start Founder OS"
    },
    {
      title: "Enterprise Media Build",
      subtitle: "The Asset",
      description: "For firms expanding into B2C. We build a standalone media brand for your company.",
      includes: [
        "Example: A Quant fund launching a \"Market Mathematics\" channel",
        "Concept, Casting, Production",
        "Distribution Strategy",
        "Monetization Strategy"
      ],
      price: "Custom",
      priceNote: "Scope + Performance/Equity",
      cta: "Discuss Enterprise"
    }
  ];

  const whyChooseUs = [
    {
      title: "Content Analysis",
      description: "We run all the videos through our proprietary content intelligence system, which analyzes hooks, retention, and audience response to engineer growth."
    },
    {
      title: "Acceleration System",
      description: "You'll see measurable traction within 30 days, or we'll work double the volume until you do at no extra cost."
    },
    {
      title: "90 Days Assurance",
      description: "If after 90 days you don't have content reach and authority that matches our case studies, we return 100% money, no questions asked."
    },
    {
      title: "On-Time Delivery",
      description: "All our deliverables are shot, edited, and delivered on schedule - no delays, ever. If we miss, we deliver the next month's content free."
    }
  ];

  const testimonials = [
    {
      quote: "These people are the real commandos of content",
      name: "Shivender Kanwar",
      title: "Ex Para SF (YouTube channel)",
      image: "https://framerusercontent.com/images/UWPvLAi5YrL7SazOLSfhyPqXk.jpg?width=6000&height=4000"
    },
    {
      quote: "You are the real MVP",
      name: "Rahul Seth",
      title: "Director - Antler | Industrial 47",
      image: "https://framerusercontent.com/images/spuc2ytXfj48L2KdzHw1irAWa8.jpeg?width=738&height=738"
    },
    {
      quote: "Everyone in the office loved it. Great Job!",
      name: "Arushi Nayak",
      title: "Social media manager at WTFund",
      image: "https://framerusercontent.com/images/6zcr8WIETfAZyko0I37u4w1kYw.png?width=462&height=494"
    },
    {
      quote: "You guys are the wizards",
      name: "Ariana Koyel",
      title: "Social Media Influencer",
      image: "https://framerusercontent.com/images/bVjkbOzZTKN6FDBqgIlcuWHjU.png?width=892&height=922"
    }
  ];

  const faqs = [
    {
      question: "How is Attention Labs different from traditional agencies?",
      answer: "We focus on video-led growth with proprietary content intelligence systems that analyze and optimize every piece of content for maximum impact."
    },
    {
      question: "Can you guarantee results?",
      answer: "Yes. We offer a 30-day acceleration guarantee and a 90-day money-back assurance if you don't see results matching our case studies."
    },
    {
      question: "What's your pricing structure?",
      answer: "Our pricing is customized based on your needs and scale. Book a call to discuss your specific requirements."
    },
    {
      question: "Is there a lock-in contract?",
      answer: "We believe in earning your business every month. Our contracts are flexible and designed around your growth goals."
    },
    {
      question: "Why should I choose you over hiring an in-house team?",
      answer: "We provide a complete content ecosystem with proven systems, faster execution, and guaranteed results - all at a fraction of the cost of building an in-house team."
    },
    {
      question: "Do you only edit, or do you also shoot content?",
      answer: "We offer full-service production including strategy, scripting, shooting, editing, and distribution. We can also work with your existing footage."
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Fixed Background Animation */}
      <div className="fixed inset-0 z-0">
        <PixelBlast
          variant="square"
          pixelSize={4}
          color="#B19EEF"
          patternScale={2}
          patternDensity={1}
          pixelSizeJitter={0}
          enableRipples={false}
          liquid={false}
          speed={0.5}
          edgeFade={0.25}
          transparent={false}
        />
      </div>

      <div className="relative z-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: showContent ? 1 : 0 }}
          transition={{ duration: 1 }}
        >
          <Header />
        </motion.div>

        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6"
              style={{ fontFamily: 'Space Grotesk, sans-serif', lineHeight: '1.2' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: showContent ? 1 : 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            >
              <div>
                <TypewriterText 
                  words={['Distribution', 'is', 'the', 'Only', 'Moat', 'Left.']} 
                  delay={500}
                  speed={100}
                  wordDelay={200}
                  onComplete={() => setTypingComplete(true)}
                />
              </div>
            </motion.div>
            <motion.p 
              className="text-lg sm:text-xl md:text-2xl text-white/70 max-w-4xl mx-auto mb-12" 
              style={{ fontFamily: 'Inter, sans-serif' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 20 }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              In an age of infinite content supply, attention is the scarcest asset. We build Media Infrastructure for high-net-worth founders and enterprises to capture, hold, and monetize that attention.
            </motion.p>
            <motion.a 
              href="#contact" 
              className="inline-block px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-white/90 transition-all text-lg"
              style={{ fontFamily: 'Inter, sans-serif' }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: showContent ? 1 : 0, scale: showContent ? 1 : 0.8 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Start The Protocol Sprint
            </motion.a>
            <motion.p 
              className="text-sm text-white/50 mt-4" 
              style={{ fontFamily: 'Inter, sans-serif' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: showContent ? 1 : 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Accepting clients for Q1 2026.
            </motion.p>
          </div>
          
          {/* Scroll indicator */}
          <motion.div 
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2"
            initial={{ opacity: 0, y: -20 }}
            animate={{ 
              opacity: typingComplete ? 1 : 0, 
              y: typingComplete ? [0, 10, 0] : -20 
            }}
            transition={{ 
              opacity: { duration: 0.5, delay: 0.5 },
              y: { 
                duration: 1.5, 
                repeat: Infinity, 
                ease: "easeInOut",
                delay: 0.5
              }
            }}
          >
            <ChevronDown className="text-white/50" size={24} />
          </motion.div>
        </section>

        <AnimatedLine />

        {/* All sections below hero have black background */}
        <div className="bg-black">
        
        {/* The Thesis Section */}
        <section className="relative py-24 sm:py-32">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-12 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Stop Renting. Start Owning.
            </h2>
            
            <div className="space-y-8 text-lg sm:text-xl text-white/80" style={{ fontFamily: 'Inter, sans-serif' }}>
              <p>
                Most companies rely on <span className="text-white font-semibold">"Rented Attention"</span> (Paid Ads). The moment you stop paying, the traffic stops. That is a liability.
              </p>
              <p>
                We build <span className="text-white font-semibold">"Owned Attention"</span> (Media Assets). This acts like equity. It compounds over time, lowers your CAC to near-zero, and builds a defensive moat around your business.
              </p>
            </div>
          </div>
        </section>

        <AnimatedLine />

        {/* The Who Section */}
        <section className="relative py-24 sm:py-32">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-16 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Who We Engineer Attention For
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="border border-white/10 rounded-lg p-8 hover:border-white/30 transition-colors">
                <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  The Technical Founder
                </h3>
                <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                  You have a world-changing product but zero distribution. We translate your code into culture.
                </p>
              </div>

              <div className="border border-white/10 rounded-lg p-8 hover:border-white/30 transition-colors">
                <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  The Investment Firm
                </h3>
                <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                  You have AUM but no public trust. We build the educational media arm that fills your funnel.
                </p>
              </div>

              <div className="border border-white/10 rounded-lg p-8 hover:border-white/30 transition-colors">
                <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  The Enterprise
                </h3>
                <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                  You are tired of "Marketing Agencies" burning cash. You want an auditable, data-driven media asset.
                </p>
              </div>
            </div>
          </div>
        </section>

        <AnimatedLine />

        {/* Case Studies Section */}
        <section className="relative py-24 sm:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Case Studies
            </h2>
            <p className="text-center text-white/70 mb-16 max-w-2xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
              Real results from founders and enterprises who trusted us with their attention infrastructure.
            </p>
            <FocusCards cards={caseStudies} />
          </div>
        </section>

        <AnimatedLine />
        
        {/* Portfolio Clients Section */}
        <section className="relative py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-center text-sm font-semibold text-white/50 mb-12 tracking-wider" style={{ fontFamily: 'Inter, sans-serif' }}>
              PORTFOLIO CLIENTS
            </h2>
            
            {/* Infinite scroll animation */}
            <div className="overflow-hidden">
              <div className="flex gap-12 animate-scroll w-max">
                {[...portfolioClients, ...portfolioClients, ...portfolioClients, ...portfolioClients].map((client, idx) => (
                  <div key={idx} className="flex-shrink-0">
                    <img src={client} alt="Client logo" className="h-12 w-auto opacity-50 hover:opacity-100 transition-opacity" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <AnimatedLine />

        {/* Stats Section */}
        <section className="relative lg:min-h-screen flex flex-col lg:flex-row">
          <div className="w-full lg:w-1/2 flex flex-col justify-center px-4 sm:px-6 md:px-8 lg:px-16 py-12 sm:py-16 lg:py-24">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-12">
              <div>
                <div className="text-6xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                  1000+
                </div>
                <div className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Videos Created
                </div>
              </div>
              <div>
                <div className="text-6xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                  200+
                </div>
                <div className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Clients Served
                </div>
              </div>
              <div>
                <div className="text-6xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                  80M+
                </div>
                <div className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Views Generated
                </div>
              </div>
            </div>
          </div>

          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0">
            <AnimatedVerticalLine delay={0.5} />
          </div>

          <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-2xl">
              {videos.map((video, idx) => (
                <a 
                  key={idx}
                  href={`https://youtube.com/watch?v=${video.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative group overflow-hidden rounded-lg aspect-video"
                >
                  <img 
                    src={video.thumbnail} 
                    alt="Video thumbnail" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <Play className="text-white" size={48} />
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        <AnimatedLine />

        {/* Services Section */}
        <section id="services" className="relative py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Deployment Options
            </h2>
            <p className="text-center text-white/70 mb-16 text-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
              Three ways to work with us
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {services.map((service, idx) => (
                <div key={idx} className="border border-white/10 rounded-lg p-8 hover:border-white/30 transition-colors flex flex-col">
                  <div className="text-sm text-white/50 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {service.subtitle}
                  </div>
                  <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {service.title}
                  </h3>
                  <p className="text-white/70 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {service.description}
                  </p>
                  <div className="mb-6">
                    <p className="text-sm font-semibold mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>Deliverables:</p>
                    <ul className="space-y-2">
                      {service.includes.map((item, i) => (
                        <li key={i} className="text-white/70 text-sm flex items-start gap-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                          <span className="text-purple-400 mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-auto">
                    <div className="mb-4">
                      <div className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                        {service.price}
                      </div>
                      <div className="text-sm text-white/50" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {service.priceNote}
                      </div>
                    </div>
                    <a 
                      href="#contact"
                      className="block w-full text-center px-6 py-3 border border-white/20 rounded-lg hover:bg-white/10 transition-colors font-semibold"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {service.cta}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <AnimatedLine />

        {/* Why Choose Us Section */}
        <section className="relative lg:min-h-screen flex flex-col lg:flex-row">
          <div className="w-full lg:w-1/2 flex flex-col justify-center px-4 sm:px-6 md:px-8 lg:px-16 py-12 sm:py-16 lg:py-24">
            <h2 className="text-4xl sm:text-5xl font-bold mb-12" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Why Choose Us?
            </h2>
            
            <div className="space-y-8">
              {whyChooseUs.map((item, idx) => (
                <div key={idx}>
                  <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {item.title}
                  </h3>
                  <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0">
            <AnimatedVerticalLine delay={0.5} />
          </div>

          <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
            <div className="w-full max-w-md aspect-square bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-3xl"></div>
          </div>
        </section>

        <AnimatedLine />

        {/* How We Operate Section */}
        <section className="relative py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-16 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              How We Operate?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              <div className="text-center">
                <div className="text-6xl font-bold mb-4 text-purple-400" style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                  01
                </div>
                <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Capture
                </h3>
                <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                  We get your content in front of people who'll actually buy, through sharp storytelling and smart distribution.
                </p>
              </div>
              
              <div className="text-center">
                <div className="text-6xl font-bold mb-4 text-purple-400" style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                  02
                </div>
                <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Monetize
                </h3>
                <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Turn attention into revenue. Whether it's products, services, or partnerships, we build real business models around what you're already creating.
                </p>
              </div>
              
              <div className="text-center">
                <div className="text-6xl font-bold mb-4 text-purple-400" style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                  03
                </div>
                <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Optimize
                </h3>
                <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Already in motion? We'll streamline your systems and growth strategy so you scale without the burnout.
                </p>
              </div>
            </div>
          </div>
        </section>

        <AnimatedLine />

        {/* Testimonials Section */}
        <section className="relative py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Testimonials
            </h2>
            <p className="text-center text-white/70 mb-16" style={{ fontFamily: 'Inter, sans-serif' }}>
              What clients say about our services
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {testimonials.map((testimonial, idx) => (
                <div key={idx} className="border border-white/10 rounded-lg p-6 hover:border-white/30 transition-colors">
                  <div className="mb-4">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  </div>
                  <p className="text-lg mb-4 italic" style={{ fontFamily: 'Inter, sans-serif' }}>
                    "{testimonial.quote}"
                  </p>
                  <div>
                    <p className="font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {testimonial.name}
                    </p>
                    <p className="text-sm text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {testimonial.title}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <AnimatedLine />

        {/* FAQs Section */}
        <section className="relative py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              FAQs
            </h2>
            <p className="text-center text-white/70 mb-16" style={{ fontFamily: 'Inter, sans-serif' }}>
              Quick answers to your questions.
            </p>
            
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="border border-white/10 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFAQ(openFAQ === idx ? null : idx)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                  >
                    <span className="font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {faq.question}
                    </span>
                    <ChevronDown 
                      className={`transition-transform ${openFAQ === idx ? 'rotate-180' : ''}`}
                      size={20}
                    />
                  </button>
                  {openFAQ === idx && (
                    <div className="px-6 py-4 bg-white/5 border-t border-white/10">
                      <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <AnimatedLine />

        {/* Final CTA Section */}
        <section id="contact" className="relative py-24 sm:py-32">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Let's Capture and Monetize attention for you
            </h2>
            <p className="text-xl text-white/70 mb-12" style={{ fontFamily: 'Inter, sans-serif' }}>
              Book a call and discover how we scale brands through video-led growth
            </p>
            <a 
              href="https://calendly.com/contact-attentionlabs/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-white/90 transition-all text-lg"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Book a free call
            </a>
          </div>
        </section>

        <AnimatedLine />

        {/* Footer */}
        <footer className="relative py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <img 
                    src="https://framerusercontent.com/images/mCmSpcpcYCZJTSJSoearkxxG0FQ.png?width=236&height=240" 
                    alt="AttentionLabs" 
                    className="h-10 w-10"
                  />
                  <span className="text-white font-bold text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    AttentionLabs
                  </span>
                </div>
                <p className="text-white/70 text-sm mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                  811, Tower C, Unitech Business Zone<br />
                  Sector 50, Gurugram, India
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Links</h3>
                <ul className="space-y-2 text-sm">
                  <li><a href="#clients" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Clients</a></li>
                  <li><a href="#portfolio" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Portfolio</a></li>
                  <li><a href="#why-choose-us" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Why Choose Us</a></li>
                  <li><a href="#testimonials" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Testimonials</a></li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Pages</h3>
                <ul className="space-y-2 text-sm">
                  <li><a href="/" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Home</a></li>
                  <li><a href="/portfolio" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Portfolio</a></li>
                  <li><a href="/casestudies" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Case Studies</a></li>
                  <li><a href="/contact" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Contact</a></li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Socials</h3>
                <ul className="space-y-2 text-sm">
                  <li><a href="https://www.instagram.com/attention.labs/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>Instagram</a></li>
                  <li><a href="https://www.youtube.com/@AttentionLabs-br" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>YouTube</a></li>
                  <li><a href="https://www.linkedin.com/company/attentionlabscom/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>LinkedIn</a></li>
                </ul>
              </div>
            </div>

            <div className="border-t border-white/10 pt-8 text-center">
              <p className="text-white/50 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                Visioned and Crafted by AttentionLabs © All rights reserved
              </p>
            </div>
          </div>
        </footer>
        
        </div>
        {/* End black background wrapper */}
        
      </div>
    </div>
  );
};

export default Alt1Landing;
